import I18n from "i18n-js";
import React from "react";
import moment from "moment";
import { arrayOf, func, shape } from "prop-types";
import { connect } from "react-redux";
import List from "../../common/components/layout/List";
import Table from "../../common/components/elements/Table";
import Quantity from "../../common/components/elements/Quantity";
import DateInput from "../../common/components/form/DateInput";
import {
  getFluidSummariesList,
  updateFluidSummariesList,
} from "../actions/fluidSummariesActions";
import { ButtonGroup, Button } from "react-bootstrap";
import DownloadButton from "../../common/components/elements/DownloadButton";
import { fetch } from "../../../appContainer";
import NoData from "../../common/components/elements/NoData";

const UNITS = ["kg", "CO2"];

class FluidSummaryList extends List {
  static propTypes = {
    fluidSummaries: shape().isRequired,
    fluidSummariesList: arrayOf(shape).isRequired,
    getFluidSummariesList: func.isRequired,
    updateFluidSummariesList: func.isRequired,
  };

  constructor() {
    super();

    this.state.filters.startDate = new Date(moment().startOf("year")).getTime();
    this.state.filters.endDate = new Date(moment().endOf("year")).getTime();
    this.state.unit = UNITS[0];

    this.refreshDataState = this.refreshDataState.bind(this);
    this.exportToTsv = this.exportToTsv.bind(this);
    this.dataTableRow = this.dataTableRow.bind(this);
    this.renderActions = this.renderActions.bind(this);
  }

  /**
   * {@inheritdoc}
   */
  componentDidMount() {
    super.componentDidMount();

    const { startDate, endDate } = this.state.filters;

    this.props.getFluidSummariesList(
      new Date(parseInt(startDate)),
      new Date(parseInt(endDate))
    );
  }

  /**
   * @param {Object} prevState
   */
  refreshDataState(prevState) {
    const { startDate, endDate } = this.state.filters;

    if (
      prevState.filters.startDate === startDate &&
      prevState.filters.endDate === endDate
    ) {
      return;
    }

    this.props.updateFluidSummariesList(
      new Date(parseInt(startDate)),
      new Date(parseInt(endDate))
    );
  }

  exportToTsv() {
    const { unit: selectedUnit } = this.state;
    const { startDate, endDate } = this.state.filters;

    return new Promise((download) => {
      fetch("api").exportFluidSummaries(
        new Date(parseInt(startDate)),
        new Date(parseInt(endDate)),
        selectedUnit,
        (tsvContent) => download(tsvContent),
        (error) => {
          console.error(error);
          download(null);
        }
      );
    });
  }

  /**
   * @return {Array}
   */
  dataTableHead() {
    return [
      I18n.t("pages.fluid_summaries.list.head.fluid_designation"),
      I18n.t("pages.fluid_summaries.list.head.filling_quantity_maintenance"),
      I18n.t("pages.fluid_summaries.list.head.filling_quantity_commissioning"),
      I18n.t("pages.fluid_summaries.list.head.maintenance_quantity"),
      I18n.t("pages.fluid_summaries.list.head.out_of_use_quantity"),
      I18n.t("pages.fluid_summaries.list.head.reuse_quantity"),
      I18n.t("pages.fluid_summaries.list.head.treatment_quantity"),
      I18n.t("pages.fluid_summaries.list.head.recycled_quantity"),
      I18n.t("pages.fluid_summaries.list.head.number_of_interventions"),
      I18n.t("pages.fluid_summaries.list.head.site_designation"),
    ];
  }

  /**
   * @param {Object} rowData A single fluid usage summary item
   *
   * @return {Array}
   */
  dataTableRow(rowData) {
    const { unit } = this.state;

    let { fluidName, numberOfInterventions, siteDesignation } = rowData;

    let fillingQuantityMaintenance,
      fillingQuantityCommissioning,
      maintenanceQuantity,
      outOfUseQuantity,
      reuseQuantity,
      treatmentQuantity,
      recycledQuantity;

    if (unit === "kg") {
      fillingQuantityMaintenance = rowData.fillingQuantityMaintenanceKg;
      fillingQuantityCommissioning = rowData.fillingQuantityCommissioningKg;
      maintenanceQuantity = rowData.maintenanceQuantityKg;
      outOfUseQuantity = rowData.outOfUseQuantityKg;
      reuseQuantity = rowData.reuseQuantityKg;
      treatmentQuantity = rowData.treatmentQuantityKg;
      recycledQuantity = rowData.recycledQuantityKg;
    } else if (unit === "CO2") {
      fillingQuantityMaintenance = rowData.fillingQuantityMaintenanceCO2;
      fillingQuantityCommissioning = rowData.fillingQuantityCommissioningCO2;
      maintenanceQuantity = rowData.maintenanceQuantityCO2;
      outOfUseQuantity = rowData.outOfUseQuantityCO2;
      reuseQuantity = rowData.reuseQuantityCO2;
      treatmentQuantity = rowData.treatmentQuantityCO2;
      recycledQuantity = rowData.recycledQuantityCO2;
    }

    return [
      <span key="fluidName" className="text-nowrap">
        {fluidName}
      </span>,
      <Quantity
        key="fillingQuantityMaintenance"
        value={fillingQuantityMaintenance}
        unit={unit}
      />,
      <Quantity
        key="fillingQuantityCommissioning"
        value={fillingQuantityCommissioning}
        unit={unit}
      />,
      <Quantity
        key="maintenanceQuantity"
        value={maintenanceQuantity}
        unit={unit}
      />,
      <Quantity key="outOfUseQuantity" value={outOfUseQuantity} unit={unit} />,
      <Quantity key="reuseQuantity" value={reuseQuantity} unit={unit} />,
      <Quantity
        key="treatmentQuantity"
        value={treatmentQuantity}
        unit={unit}
      />,
      <Quantity key="recycledQuantity" value={recycledQuantity} unit={unit} />,
      numberOfInterventions,
      siteDesignation ?? (
        <div style={{ whiteSpace: "nowrap" }}>
          <NoData />
        </div>
      ),
    ];
  }

  /**
   * {@inheritdoc}
   */
  renderFilters() {
    const { filters } = this.state;

    return [
      <DateInput
        key="filterByStartDate"
        id="filterByStartDate"
        onChange={(date) =>
          this.onChangeDateFilter(date, "startDate", this.refreshDataState)
        }
        label={I18n.t("filters.by_start_date")}
        defaultValue={
          filters.startDate && new Date(parseInt(filters.startDate))
        }
        legend
        optional
        isClearable
      />,
      <DateInput
        key="filterByEndDate"
        id="filterByEndDate"
        onChange={(date) =>
          this.onChangeDateFilter(date, "endDate", this.refreshDataState)
        }
        label={I18n.t("filters.by_end_date")}
        defaultValue={filters.endDate && new Date(parseInt(filters.endDate))}
        legend
        optional
        isClearable
      />,
    ];
  }

  renderActions() {
    const { unit: selectedUnit } = this.state;

    return (
      <>
        <ButtonGroup>
          {UNITS.map((unit) => (
            <Button
              key={unit}
              className="mb-0"
              variant={this.state.unit === unit ? "primary" : "outline-secondary"}
              onClick={() => this.setState({ unit })}
            >
              {I18n.t(`common.quantity.unit:${unit}`)}
            </Button>
          ))}
        </ButtonGroup>
        <DownloadButton
          className="ml-2"
          fileContent={this.exportToTsv}
          filename={`climapp_fluid_synthesis_${selectedUnit}_${moment().format(
            "YYYY-MM-DD_HH-mm-ss"
          )}.tsv`}
          label={I18n.t("common.actions.export")}
          contentType="text/csv;charset=utf8;"
        />
      </>
    );
  }

  /**
   * {@inheritdoc}
   */
  renderContent() {
    const { fluidSummaries, fluidSummariesList } = this.props;

    if (fluidSummaries.inProgress) {
      return <div className="loader" />;
    }

    if (fluidSummaries.error) {
      return <p>{I18n.t("common.loadingApiError")}</p>;
    }

    return (
      <Table
        tableClasses="table--selectable"
        emptyContent={I18n.t("pages.fluid_summaries.list.no_content")}
        data={fluidSummariesList}
        headData={this.dataTableHead()}
        rowData={this.dataTableRow}
        pagination={false}
        filters={this.renderFilters()}
        actions={this.renderActions()}
      />
    );
  }

  /**
   * {@inheritdoc}
   */
  renderTitle() {
    return I18n.t("pages.fluid_summaries.list.title");
  }
}

const mapStateToProps = (state) => ({
  fluidSummaries: state.fluidSummaries,
  fluidSummariesList: state.fluidSummaries.list,
});

const mapDispatchToProps = (dispatch) => ({
  getFluidSummariesList: (startDate, endDate) =>
    dispatch(getFluidSummariesList(startDate, endDate)),
  updateFluidSummariesList: (startDate, endDate) =>
    dispatch(updateFluidSummariesList(startDate, endDate)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FluidSummaryList);
